import React, { useCallback, useEffect, useState } from 'react';

/**
 * Catalogue › Appli mobile (01/10/2026, packs le 04/10/2026 ; achats intégrés
 * le 02/11/2026) : ce que l'appli SEmply VEND, un interrupteur par système et
 * par usage :
 *
 *  - les FORMULES (abonnements de l'App Store et de Google Play) ;
 *  - les PACKS (produits « consommables » des stores).
 *
 * Les deux se règlent à part : on peut ouvrir l'un sans l'autre.
 *
 * ── Ce que fait un interrupteur ────────────────────────────────────────────
 *
 * Ouvert : l'appli propose les formules (ou les packs) qui ont un
 * « identifiant dans les stores », au prix fixé dans App Store Connect et la
 * Play Console, payés par le store (RevenueCat prévient le portail).
 *
 * Fermé : l'appli montre l'existant (formule en cours, crédits et achats),
 * sans rien proposer d'acheter, avec un message neutre. Apple teste l'achat
 * intégré pendant sa revue : l'interrupteur iPhone des formules doit donc être
 * OUVERT avant d'envoyer une version en revue.
 *
 * Enregistrement explicite (bouton), comme l'écran Ouverture : on ne bascule
 * pas une décision commerciale en cliquant à côté.
 */

const USAGES = [
  {
    cle: 'formule',
    titre: 'Formules en achat intégré',
    champs: { ios: 'mobilePlanChangeIos', android: 'mobilePlanChangeAndroid' },
    explication:
      'Ouvert, l’écran « Formules » de l’appli propose les abonnements des stores. Fermé, l’appli affiche la formule en cours sans rien proposer d’acheter.',
  },
  {
    cle: 'packs',
    titre: 'Packs en achat intégré',
    champs: { ios: 'mobilePackPurchaseIos', android: 'mobilePackPurchaseAndroid' },
    explication:
      'Ouvert, l’écran « Packs » de l’appli propose les packs des stores. Fermé, il montre les crédits restants et les achats, sans prix.',
  },
];

const SYSTEMES = [
  {
    cle: 'ios',
    libelle: 'iPhone (App Store)',
    condition:
      'Exige les produits dans App Store Connect (mêmes identifiants qu’au catalogue), RevenueCat configuré, et un build de l’appli qui intègre l’achat intégré.',
  },
  {
    cle: 'android',
    libelle: 'Android (Google Play)',
    condition:
      'Exige les produits dans la Play Console (mêmes identifiants qu’au catalogue), RevenueCat configuré, et un build de l’appli qui intègre l’achat intégré.',
  },
];

/** `{ formule: { ios, android }, packs: { ios, android } }` depuis les réglages. */
function lire(reglages) {
  const etat = {};
  for (const u of USAGES) {
    etat[u.cle] = {
      ios: Boolean(reglages?.[u.champs.ios]),
      android: Boolean(reglages?.[u.champs.android]),
    };
  }
  return etat;
}

export function AdminAppliMobile({ call, canWrite }) {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { reglages } = await call('/admin/ouverture');
      const lu = lire(reglages);
      setForm(lu);
      setInitial(lu);
    } catch (e) {
      setError(String(e.message ?? e));
    }
  }, [call]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const corps = {};
      for (const u of USAGES) {
        corps[u.champs.ios] = form[u.cle].ios;
        corps[u.champs.android] = form[u.cle].android;
      }
      await call('/admin/ouverture', { method: 'PATCH', body: JSON.stringify(corps) });
      setNotice(
        'Réglages enregistrés. L’appli les lit à l’ouverture de « Mon compte » et de l’écran « Packs ».',
      );
      await load();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return (
      <section className="ap-form">
        <h2>Appli mobile</h2>
        {error ? <p className="ap-error">{error}</p> : <p>Chargement…</p>}
      </section>
    );
  }

  const modifie = USAGES.some(
    (u) => form[u.cle].ios !== initial[u.cle].ios || form[u.cle].android !== initial[u.cle].android,
  );

  return (
    <section className="ap-form">
      <p className="ap-muted">
        Ce que l’appli peut ouvrir vers le site, système par système. Un lien n’apparaît que si le
        build de l’appli le prévoit aussi : sans build compatible, ouvrir ici est sans effet.
      </p>

      {USAGES.map((u) => (
        <div key={u.cle} className="ap-section">
          <h2>{u.titre}</h2>
          <p className="ap-muted">{u.explication}</p>
          {SYSTEMES.map((s) => (
            <div key={s.cle}>
              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={form[u.cle][s.cle]}
                  disabled={!canWrite || busy}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, [u.cle]: { ...f[u.cle], [s.cle]: e.target.checked } }))
                  }
                />
                {s.libelle} : {form[u.cle][s.cle] ? 'ouvert' : 'fermé'}
              </label>
              <p className="ap-hint">{s.condition}</p>
            </div>
          ))}
        </div>
      ))}

      {error && <p className="ap-error">{error}</p>}
      {notice && <p className="ap-notice">{notice}</p>}

      {canWrite && (
        <div className="ap-actions">
          <button className="ap-btn primary" onClick={save} disabled={busy || !modifie}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="ap-btn" onClick={() => setForm(initial)} disabled={busy || !modifie}>
            Annuler
          </button>
        </div>
      )}
    </section>
  );
}

export default AdminAppliMobile;
